// Third party
import Divider from '@mui/material/Divider';
import { useTranslation } from 'react-i18next';

// Hooks
import { useTheme } from '@mui/material';
import Stack from '@mui/material/Stack';
import Card from '@mui/material/Card';
import { useMemo } from 'react';
import { AnalyticItem } from '@mgm/common/components';
import ScrollBar from '@mgm/common/components/ScrollBar';
import { formatDecimalHours } from '@mgm/common/utils';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { useSheets } from '../../../../hooks/useSheets';

// Redux-toolkit
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';
import { getCurrencyById } from '../../../../store/common/selectors';

// Helpers
import { getFilteredSheets, getSelectedLocationId } from '../../../../store/sheets/selectors';
import { getPersonnelStatus } from '../../../../store/clock/selectors';
import { getMembers } from '../../../../store/team/selectors';

const TableFooter = () => {
  const activeUser = useAppSelector(getAuthenticateUser);
  const getCurrency = useAppSelector(getCurrencyById);
  const personnelStatus = useAppSelector(getPersonnelStatus);

  const theme = useTheme();

  const { t } = useTranslation(['timesheets', 'team', 'inputs']);
  const { getTimesheetsTotals } = useSheets();
  const selectedLocationId = useAppSelector(getSelectedLocationId);

  const filteredSheets = useAppSelector(getFilteredSheets);
  const members = useAppSelector(getMembers);

  const { getSheetsByFilters } = useSheets();

  const activeMembers = useMemo(() => Object.values(personnelStatus).filter(({ status, location }) => {
    // Only include active members
    if (status !== 'Active') return false;

    // If selectedLocationId is 0, return all active members
    if (selectedLocationId === 0) return true;

    // Otherwise, return members that match the selectedLocationId
    return location?.id === selectedLocationId;
  }), [personnelStatus, selectedLocationId]);

  const searchedSheets = useMemo(() => {
    const totalSheets = getSheetsByFilters?.length || members?.length;
    const totalActiveMembers = activeMembers.length;

    // Avoid division by zero
    const percentActiveMembers = totalSheets > 0 ? (totalActiveMembers / totalSheets) * 100 : 0;

    return {
      members: totalSheets,
      percentActiveMembers: `${totalActiveMembers}/${totalSheets} (${percentActiveMembers.toFixed(2)}%)`,
      ...getTimesheetsTotals(getSheetsByFilters),
    };
  }, [getSheetsByFilters, activeMembers, filteredSheets, members]);

  return (
    <Card sx={{
      height: {
        sm: '120px', lg: '88px', md: '100px',
      },
      width: '100%',
    }}
    >
      <ScrollBar>
        <Stack
          direction="row"
          divider={<Divider orientation="vertical" flexItem sx={{ borderStyle: 'dashed' }} />}
          sx={{ p: 2, overflow: 'auto !important' }}
        >
          <AnalyticItem
            title={t('members', { ns: 'team' })}
            percent={100}
            price={searchedSheets.members}
            icon="fluent:people-team-16-regular"
            color={theme.palette.info.main}
          />
          <AnalyticItem
            id="workforce"
            title={t('activeWorkforce')}
            percent={100}
            price={searchedSheets.percentActiveMembers}
            icon="fluent-mdl2:workforce-management"
            color={theme.palette.primary.main}
          />
          <AnalyticItem
            title={t('hours', { ns: 'inputs' })}
            percent={100}
            price={formatDecimalHours(searchedSheets.totalHours)}
            icon="eva:clock-fill"
            color={theme.palette.success.main}
          />

          <AnalyticItem
            title={t('amount')}
            percent={100}
            price={`${getCurrency(activeUser.personnel.paymentCurrencyId).mark} ${searchedSheets.totalAmount}`}
            icon="tabler:coin"
            color={theme.palette.warning.main}
          />
        </Stack>
      </ScrollBar>
    </Card>
  );
};

export {
  TableFooter,
};
